import React, { useEffect, useMemo } from "react";
import { useAdminUser } from "../../../context/AdminUserContext";
import { useApiMetrics } from "../../../context/ApiMetricsContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line
} from "recharts";

const ALLOWED = ["User Admin", "Support Admin", "Super Admin"];

export default function ApiStatsMonitoring() {
  const { adminUser } = useAdminUser();
  const { metrics, loading, error, fetchMetrics } = useApiMetrics();

  // Fetch when we have a valid admin
  useEffect(() => {
    if (adminUser && ALLOWED.indexOf(adminUser.assignedRole) !== -1) {
      fetchMetrics();
    }
  }, [adminUser, fetchMetrics]);

  // Safely parse metrics into two data arrays
  const { httpData, repoData } = useMemo(() => {
    const httpMap = {};
    const repoMap = {};

    // guard if metrics is null/undefined
    const text = typeof metrics === "string" ? metrics : "";
    const lines = text.split("\n");

    lines.forEach(line => {
      const trimmed = (line || "").trim();
      // skip blank lines or comments
      if (!trimmed || trimmed.charAt(0) === "#") return;

      // split metric+labels from value
      const parts = trimmed.split(/\s+/);
      if (parts.length < 2) return;
      const metricWithLabels = parts[0];
      const value = parseFloat(parts[1]);
      if (isNaN(value)) return;

      // extract name vs labels
      let metricName = metricWithLabels;
      let labelString = "";
      const i = metricWithLabels.indexOf("{");
      if (i >= 0) {
        metricName = metricWithLabels.substring(0, i);
        labelString = metricWithLabels.substring(i + 1, metricWithLabels.length - 1);
      }

      // build labels map
      const labels = {};
      if (labelString) {
        labelString.split(",").forEach(pair => {
          const eq = pair.indexOf("=");
          if (eq > 0) {
            const k = pair.substring(0, eq);
            let v = pair.substring(eq + 1);
            // strip surrounding quotes
            if (v.charAt(0) === '"' && v.charAt(v.length - 1) === '"') {
              v = v.substring(1, v.length - 1);
            }
            labels[k] = v;
          }
        });
      }

      // HTTP metrics
      if (metricName.indexOf("http_server_requests_seconds_") === 0) {
        const uri    = labels.uri    || "unknown";
        const method = labels.method || "unknown";
        const status = labels.status || "unknown";
        const key    = method + "|" + status + "|" + uri;

        if (!httpMap[key]) {
          httpMap[key] = { method, status, uri, count: 0, sum: 0, max: 0 };
        }
        if (metricName.endsWith("_count")) httpMap[key].count = value;
        else if (metricName.endsWith("_sum"))   httpMap[key].sum   = value;
        else if (metricName.endsWith("_max"))   httpMap[key].max   = value;
      }

      // Repository max
      if (metricName === "spring_data_repository_invocations_seconds_max") {
        const repo = labels.repository || "unknown";
        repoMap[repo] = value;
      }
    });

    // flatten
    const httpData = Object.keys(httpMap).map(k => {
      const d = httpMap[k];
      return { ...d, avg: d.count ? d.sum / d.count : 0 };
    });
    const repoData = Object.keys(repoMap).map(repo => ({
      repository: repo,
      max: repoMap[repo]
    }));

    return { httpData, repoData };
  }, [metrics]);

  // auth guard
  if (!adminUser) return null;
  if (ALLOWED.indexOf(adminUser.assignedRole) === -1) {
    return <p>You’re not authorized to view metrics.</p>;
  }

  return (
    <div className="application-metrics-container">
      <h2>📊 Application Metrics</h2>

      {loading && <div className="loading">Loading…</div>}
      {error   && <div className="error">Error loading metrics</div>}

      {!loading && !error && (
        <>
          <h3>HTTP Request Metrics Table</h3>
          <table className="supportclaim-support-table">
            <thead>
              <tr>
                {["Method","URI","Status","Count","Avg (s)","Max (s)"].map(col => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {httpData.map((r,i) => (
                <tr key={i}>
                  <td>{r.method}</td>
                  <td>{r.uri}</td>
                  <td>{r.status}</td>
                  <td>{r.count}</td>
                  <td>{r.avg.toFixed(3)}</td>
                  <td>{r.max.toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Average HTTP Latency (s)</h3>
          <div className="chart-container">
            <div className="chart-wrapper">
              <BarChart
                width={600}
                height={300}
                data={httpData}
                margin={{ bottom: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="uri" angle={-45} textAnchor="end" interval={0} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="avg" name="Avg Latency" />
                <Bar dataKey="max" name="Max Latency" />
              </BarChart>
            </div>
          </div>

          <h3>“findAll” Repository Max Duration (s)</h3>
          <div className="chart-container">
            <div className="chart-wrapper">
              <LineChart
                width={600}
                height={300}
                data={repoData}
                margin={{ bottom: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="repository"
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="max" name="Max Time" />
              </LineChart>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
